export type AfterRunMethod = (self?: any) => any;

/**
 * Runs a method after the decorated method executes.
 * The method receives the only parameter "self" (this / the target instance).
 * The method is always run regardless of the outcome of the decorated method (including thrown errors).
 * If the method throws an error, it will not be caught.
 *
 * @param {(self: any) => any} method to run after invocation of decorated method
 * @returns {(target, propertyKey: string, descriptor: PropertyDescriptor) => PropertyDescriptor}
 */
export function afterRun(method: AfterRunMethod) {
  return function afterRunDecorator(target, propertyKey: string, descriptor: PropertyDescriptor) {
    if (descriptor === undefined) {
      descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    }

    const original = descriptor.value; // warning: this === undefined

    descriptor.value = function (...args) { // eslint-disable-line func-names
      const self = this;
      const oldMethod = original.bind(self);
      let result;

      return (async () => {
        try {
          result = await oldMethod(...args);
        } finally {
          method.bind(self)(self);
        }

        return result;
      })();
    };

    return descriptor;
  };
}
